import { FC, ReactNode, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AbilityLoadingContext } from 'src/components/AbilityProvider';
import {
  AbilityContext,
  canAccessDevHub,
  type Actions,
  type Subjects,
} from 'src/lib/ability';

interface RoleProtectedRouteProps {
  children: ReactNode;
  subject: Subjects;
  action?: Actions;
  requireAdmin?: boolean;
}

const RoleProtectedRoute: FC<RoleProtectedRouteProps> = ({
  children,
  subject,
  action = 'read',
  requireAdmin = false,
}) => {
  const ability = useContext(AbilityContext);
  const isAbilityLoading = useContext(AbilityLoadingContext);
  const location = useLocation();

  // Wait for ability to finish loading before checking permissions
  if (isAbilityLoading) {
    return <>{children}</>;
  }

  // If requireAdmin is true, check if user can manage all (admin/superadmin)
  if (requireAdmin) {
    if (!ability.can('manage', 'all')) {
      // eslint-disable-next-line no-console
      console.warn(
        '[CASL RoleProtectedRoute] Access denied: Admin role required',
        location.pathname,
      );
      return <Navigate to="/home" replace />;
    }
    return <>{children}</>;
  }

  // Check if user can perform the action on the subject.
  // - Dev Hub index: any Dev Hub section permission
  // - Dev Hub pages: section permission OR legacy umbrella read Developers
  const allowed =
    ability.can(action, subject) ||
    ability.can('manage', 'all') ||
    (subject === 'Developers' && canAccessDevHub(ability)) ||
    (subject.startsWith('DevHub') && ability.can('read', 'Developers'));

  if (!allowed) {
    // eslint-disable-next-line no-console
    console.warn(
      '[CASL RoleProtectedRoute] Access denied:',
      `Cannot ${action} ${subject}`,
      location.pathname,
    );
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
